import axios from "@/lib/axios";
import { useRoleStore } from "@/stores/role";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { storeToRefs } from "pinia";
import { watch } from "vue";
import { ToastInterface, useToast } from "vue-toastification";
import { Role } from "./useRoles";

interface Params {
  id: number;
  immediate?: boolean;
}

type UpdateRole = Omit<Role, "id" | "companyId" | "usersCount" | "title">;

const getRole = async (id: number): Promise<Role> => {
  const { data } = await axios.get<Role>(`/api/roles/${id}`);

  return data;
};

const updateRole = async (
  id: number,
  role: UpdateRole,
  toast: ToastInterface,
) => {
  try {
    await axios.put(`/api/roles/${id}`, role);
    toast.success("Rol actualizado correctamente");
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const deleteRole = async (id: number | string, toast: ToastInterface) => {
  try {
    await axios.delete(`/api/roles/${id}`);

    console.log("Role deleted");
  } catch (error: any) {
    toast.error(error.response.data.message);
  }
};

const useRole = (props: Params) => {
  const { role } = storeToRefs(useRoleStore());
  const { setRole } = useRoleStore();
  const toast = useToast();

  const { data, isLoading, refetch } = useQuery(
    ["role", props.id],
    () => getRole(props.id),
    {
      enabled: props.immediate,
    },
  );

  const updateRoleMutation = useMutation((role: UpdateRole) =>
    updateRole(props.id, role, toast),
  );

  const deleteRoleMutation = useMutation((id: string | number) =>
    deleteRole(id, toast),
  );

  watch(
    data,
    (value) => {
      if (value) {
        setRole(value);
      }
    },
    {
      immediate: props.immediate,
    },
  );

  return {
    role,
    isLoading,
    refetch,

    updateRoleMutation,
    updateRoleMutationAsync: updateRoleMutation.mutateAsync,
    updateRoleMutationLoading: updateRoleMutation.isLoading,

    deleteRoleMutation,
    deleteRoleMutationAsync: deleteRoleMutation.mutateAsync,
    deleteRoleMutationLoading: deleteRoleMutation.isLoading,
  };
};

export default useRole;
